import { forwardRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../../lib/utils.js";

interface PaginationBaseProps {
  readonly className?: string | undefined;
}

export interface NumberedPaginationProps extends PaginationBaseProps {
  readonly mode: "numbered";
  readonly page: number;
  readonly pageCount: number;
  readonly onChange: (page: number) => void;
}

interface CursorPaginationCommonProps extends PaginationBaseProps {
  readonly mode: "cursor";
  readonly page: number;
  readonly totalCount?: number | undefined;
  readonly canGoBack: boolean;
  readonly hasNext: boolean;
  readonly busy: boolean;
  readonly onPrev: () => void;
  readonly onNext: () => void;
  /** Accessible name for the Previous button. Defaults to "Previous page". */
  readonly prevLabel?: string | undefined;
  /** Accessible name for the Next button. Defaults to "Next page". */
  readonly nextLabel?: string | undefined;
}

export type CursorPaginationProps = CursorPaginationCommonProps &
  (
    | {
        /** Replaces the computed "Page n · x items" text. */
        readonly label?: undefined;
        readonly pageItemCount: number;
      }
    | {
        readonly label: string;
        readonly pageItemCount?: number | undefined;
      }
  );

export type PaginationProps = NumberedPaginationProps | CursorPaginationProps;

const CONTROL =
  "inline-flex h-9 min-w-9 items-center justify-center rounded-md border border-pb-control-border bg-pb-card px-2 text-sm font-medium text-pb-text-secondary transition-colors hover:pb-overlay-paint hover:text-pb-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pb-teal/50 active:bg-pb-card-elevated disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-none";

function NumberedPagination({
  page,
  pageCount,
  onChange,
}: NumberedPaginationProps) {
  const go = (requestedPage: number) => {
    const nextPage = Math.max(1, Math.min(pageCount, requestedPage));
    if (nextPage !== page) onChange(nextPage);
  };

  const pages: (number | "…")[] = [1];
  const firstCandidate = Math.max(2, page - 1);
  const lastCandidate = Math.min(pageCount - 1, page + 1);
  if (firstCandidate > 2) pages.push("…");
  for (
    let candidate = firstCandidate;
    candidate <= lastCandidate;
    candidate += 1
  ) {
    pages.push(candidate);
  }
  if (lastCandidate < pageCount - 1) pages.push("…");
  if (pageCount > 1) pages.push(pageCount);

  const seen = new Set<number>();
  const uniquePages = pages.filter((candidate) => {
    if (candidate === "…") return true;
    if (seen.has(candidate)) return false;
    seen.add(candidate);
    return true;
  });

  return (
    <>
      <button
        type="button"
        onClick={() => go(page - 1)}
        disabled={page <= 1}
        className={CONTROL}
        aria-label="Previous page"
      >
        <ChevronLeft className="h-4 w-4 rtl:rotate-180" aria-hidden />
      </button>
      {uniquePages.map((candidate, index) =>
        candidate === "…" ? (
          <span
            key={`gap-${index}`}
            className="select-none px-1 text-sm text-pb-text-muted"
            aria-hidden
          >
            …
          </span>
        ) : (
          <button
            key={candidate}
            type="button"
            onClick={() => go(candidate)}
            aria-current={candidate === page ? "page" : undefined}
            aria-label={`Page ${candidate}`}
            className={cn(
              CONTROL,
              candidate === page &&
                "border-pb-teal/50 bg-pb-teal-soft text-pb-teal-text",
            )}
          >
            {candidate}
          </button>
        ),
      )}
      <button
        type="button"
        onClick={() => go(page + 1)}
        disabled={page >= pageCount}
        className={CONTROL}
        aria-label="Next page"
      >
        <ChevronRight className="h-4 w-4 rtl:rotate-180" aria-hidden />
      </button>
    </>
  );
}

function CursorPagination(props: CursorPaginationProps) {
  const {
    page,
    totalCount,
    canGoBack,
    hasNext,
    busy,
    onPrev,
    onNext,
    prevLabel = "Previous page",
    nextLabel = "Next page",
  } = props;

  const position =
    props.label !== undefined
      ? props.label
      : totalCount === undefined
        ? `Page ${page} · ${props.pageItemCount} item${props.pageItemCount === 1 ? "" : "s"}`
        : `Page ${page} · ${props.pageItemCount} of ${totalCount} item${totalCount === 1 ? "" : "s"}`;

  return (
    <>
      <button
        type="button"
        onClick={onPrev}
        disabled={!canGoBack || busy}
        className={CONTROL}
        aria-label={prevLabel}
      >
        <ChevronLeft className="h-4 w-4 rtl:rotate-180" aria-hidden />
      </button>
      <span
        aria-live="polite"
        aria-atomic="true"
        className="min-w-[8.5rem] px-2 text-center text-sm font-medium tabular-nums text-pb-text"
      >
        {busy ? "Loading…" : position}
      </span>
      <button
        type="button"
        onClick={onNext}
        disabled={!hasNext || busy}
        className={CONTROL}
        aria-label={nextLabel}
      >
        <ChevronRight className="h-4 w-4 rtl:rotate-180" aria-hidden />
      </button>
    </>
  );
}

/**
 * Shared pagination control. Exact result sets use numbered mode; opaque
 * keyset cursors use Previous/Next and never invent a page count.
 */
export const Pagination = forwardRef<HTMLElement, PaginationProps>(
  function Pagination(props, ref) {
    return (
      <nav
        ref={ref}
        aria-label="Pagination"
        className={cn("inline-flex items-center gap-1", props.className)}
      >
        {props.mode === "numbered" ? (
          <NumberedPagination {...props} />
        ) : (
          <CursorPagination {...props} />
        )}
      </nav>
    );
  },
);
